/**
 * StratIQX Service Configuration Utilities
 * Helper functions for working with service configuration
 */

import { SERVICES_CONFIG } from './services.config';
import { ServiceConfig, ServiceSummary, ServiceOption, PaymentServiceFormat, ValidationResult } from './types/service.types';
import { getServiceIcon, DEFAULT_SERVICE_ICON } from './icons.config';

export class ServiceConfigUtils {
  // Initialize computed deposit amounts on class load
  static {
    this.computeDepositAmounts();
  }
  /**
   * Get all active services sorted by display order
   */
  static getActiveServices(): ServiceConfig[] {
    return Object.values(SERVICES_CONFIG)
      .filter(service => service.active && !service.comingSoon)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }

  /**
   * Get all coming soon services (only those marked as coming soon)
   */
  static getComingSoonServices(): ServiceConfig[] {
    return Object.values(SERVICES_CONFIG)
      .filter(service => service.comingSoon === true)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }

  /**
   * Get service by ID
   */
  static getService(serviceId: string): ServiceConfig | null {
    return SERVICES_CONFIG[serviceId] || null;
  }

  /**
   * Get all services (active and inactive)
   */
  static getAllServices(): ServiceConfig[] {
    return Object.values(SERVICES_CONFIG)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }

  /**
   * Get service IDs
   */
  static getServiceIds(activeOnly: boolean = true): string[] {
    const services = activeOnly ? this.getActiveServices() : this.getAllServices();
    return services.map(service => service.id);
  }

  /**
   * Calculate deposit amount based on service configuration
   */
  static calculateDepositAmount(service: ServiceConfig): number {
    // Free services have no deposit
    if (service.price.value === 0) {
      return 0;
    }
    
    switch (service.deposit.type) {
      case 'none':
        return 0;
      case 'fixed':
        return service.deposit.value || 0;
      case 'percentage':
        const percentage = (service.deposit.value || 0) / 100;
        return Math.round(service.price.value * percentage);
      case 'full-price':
        return service.price.value;
      default:
        return 0;
    }
  }

  /**
   * Update service deposit amounts (computed fields)
   */
  static computeDepositAmounts(): void {
    Object.values(SERVICES_CONFIG).forEach(service => {
      service.deposit.amount = this.calculateDepositAmount(service);
    });
  }

  /**
   * Generate service summary for sidebar components
   */
  static getServiceSummary(): ServiceSummary {
    const activeServices = this.getActiveServices();
    const comingSoonServices = this.getComingSoonServices();
    const allServices = [...activeServices, ...comingSoonServices];
    
    const serviceNames = activeServices.map(s => s.name).join(' • ');
    const hasFreeServices = activeServices.some(s => s.price.value === 0);
    
    return {
      serviceList: serviceNames || 'No services available',
      activeCount: activeServices.length,
      comingSoonCount: comingSoonServices.length,
      hasFreeServices
    };
  }

  /**
   * Transform service config to landing page format
   */
  static toLandingPageFormat(service: ServiceConfig) {
    return {
      icon: getServiceIcon(service.icon),
      title: service.name,
      description: service.description,
      price: service.price.display,
      features: service.features,
      comingSoon: service.comingSoon || false,
      popular: service.popular || false
    };
  }

  /**
   * Transform service config to payment page format
   */
  static toPaymentPageFormat(service: ServiceConfig): ServiceOption {
    return {
      id: service.id,
      icon: getServiceIcon(service.icon),
      title: service.name,
      description: service.description,
      price: service.price.display,
      priceDetail: service.billingType === 'monthly' ? 'per month' : 'per engagement',
      features: service.features,
      popular: service.popular || false
    };
  }

  /**
   * Transform service config to payment service format
   */
  static toPaymentServiceFormat(service: ServiceConfig): PaymentServiceFormat {
    return {
      id: service.id,
      name: service.name,
      description: service.description,
      price: service.price,
      deposit: service.deposit,
      billingType: service.billingType,
      features: service.features,
      deliveryTime: service.metadata.deliveryTime || 'TBD'
    };
  }

  /**
   * Get popular services
   */
  static getPopularServices(): ServiceConfig[] {
    return this.getActiveServices().filter(service => service.popular);
  }

  /**
   * Get services by billing type
   */
  static getServicesByBillingType(billingType: 'one-time' | 'monthly'): ServiceConfig[] {
    return this.getActiveServices().filter(service => service.billingType === billingType);
  }

  /**
   * Get services in price range
   */
  static getServicesInPriceRange(minPrice: number, maxPrice: number): ServiceConfig[] {
    return this.getActiveServices().filter(service => 
      service.price.value >= minPrice && service.price.value <= maxPrice
    );
  }

  /**
   * Validate service configuration
   */
  static validateConfig(): ValidationResult {
    const errors: string[] = [];
    
    Object.entries(SERVICES_CONFIG).forEach(([key, service]) => {
      // Validate key matches service ID
      if (key !== service.id) {
        errors.push(`Service key '${key}' does not match service.id '${service.id}'`);
      }
      
      // Validate price
      if (service.price.value < 0) {
        errors.push(`Service '${service.id}' has invalid price: ${service.price.value}`);
      }
      
      // Validate deposit configuration
      if (service.deposit.type === 'percentage' && (!service.deposit.value || service.deposit.value <= 0 || service.deposit.value > 100)) {
        errors.push(`Service '${service.id}' has invalid percentage deposit value: ${service.deposit.value}%`);
      }
      
      if (service.deposit.type === 'fixed' && (!service.deposit.value || service.deposit.value <= 0)) {
        errors.push(`Service '${service.id}' has invalid fixed deposit value: ${service.deposit.value}`);
      }
      
      if (service.price.value > 0 && service.deposit.amount > service.price.value) {
        errors.push(`Service '${service.id}' has deposit (${service.deposit.amount}) greater than price (${service.price.value})`);
      }
      
      // Validate features
      if (!service.features || service.features.length === 0) {
        errors.push(`Service '${service.id}' has no features defined`);
      }
      
      // Validate billing type
      if (!['one-time', 'monthly'].includes(service.billingType)) {
        errors.push(`Service '${service.id}' has invalid billing type: ${service.billingType}`);
      }
      
      // Validate price display format for non-free services
      if (service.price.value > 0) {
        const expectedPriceFormat = '$' + service.price.value.toLocaleString();
        if (!service.price.display.includes(expectedPriceFormat)) {
          errors.push(`Service '${service.id}' price display '${service.price.display}' should contain formatted price '${expectedPriceFormat}'`);
        }
      }
      
      // Validate required metadata
      if (!service.metadata.deliveryTime) {
        errors.push(`Service '${service.id}' missing deliveryTime in metadata`);
      }
      
      // Validate icon exists
      if (service.icon && !getServiceIcon(service.icon)) {
        errors.push(`Service '${service.id}' has invalid icon: ${service.icon}`);
      }
    });

    // Validate at least one active service exists
    const activeServices = this.getActiveServices();
    if (activeServices.length === 0) {
      errors.push('No active services found in configuration');
    }

    // Validate unique display orders
    const displayOrders = activeServices
      .map(s => s.displayOrder)
      .filter(order => order !== undefined);
    
    const uniqueOrders = new Set(displayOrders);
    if (displayOrders.length !== uniqueOrders.size) {
      errors.push('Duplicate display orders found in active services');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate database migration SQL
   */
  static generateMigrationSQL(): string {
    const activeServices = this.getActiveServices();
    
    if (activeServices.length === 0) {
      return '-- No active services to migrate';
    }
    
    const insertStatements = activeServices.map(service => {
      const metadataJson = JSON.stringify(service.metadata).replace(/'/g, "''");
      
      return `INSERT OR REPLACE INTO services (id, name, description, price, deposit_amount, deposit_type, billing_type, active, metadata, created_at, updated_at) VALUES
  ('${service.id}', 
   '${service.name.replace(/'/g, "''")}', 
   '${service.description.replace(/'/g, "''")}', 
   ${service.price.value}, 
   ${service.deposit.amount}, 
   '${service.deposit.type}',
   '${service.billingType}',
   ${service.active ? 1 : 0},
   '${metadataJson}',
   CURRENT_TIMESTAMP,
   CURRENT_TIMESTAMP);`;
    }).join('\n\n');

    return `-- Auto-generated from services.config.ts
-- Generated at: ${new Date().toISOString()}
-- Services included: ${activeServices.map(s => s.id).join(', ')}

${insertStatements}`;
  }

  /**
   * Get configuration statistics
   */
  static getConfigStats() {
    const allServices = this.getAllServices();
    const activeServices = this.getActiveServices();
    const popularServices = this.getPopularServices();
    const oneTimeServices = this.getServicesByBillingType('one-time');
    const monthlyServices = this.getServicesByBillingType('monthly');
    
    return {
      total: allServices.length,
      active: activeServices.length,
      inactive: allServices.length - activeServices.length,
      popular: popularServices.length,
      oneTime: oneTimeServices.length,
      monthly: monthlyServices.length,
      priceRange: this.getServiceSummary(),
      validation: this.validateConfig()
    };
  }

  /**
   * Search services by name or description
   */
  static searchServices(query: string): ServiceConfig[] {
    const lowerQuery = query.toLowerCase();
    return this.getActiveServices().filter(service =>
      service.name.toLowerCase().includes(lowerQuery) ||
      service.description.toLowerCase().includes(lowerQuery) ||
      service.features.some(feature => feature.toLowerCase().includes(lowerQuery))
    );
  }
}

// Export commonly used functions for direct import
export const {
  getActiveServices,
  getService,
  getServiceSummary,
  toLandingPageFormat,
  toPaymentPageFormat,
  toPaymentServiceFormat,
  validateConfig,
  generateMigrationSQL,
  getConfigStats
} = ServiceConfigUtils;